/* eslint-disable indent */

import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
// import { Toast } from 'bootstrap';
import { transformStringIntoHTML } from '../../utils';
import {
  IEpAlertToasterInfos,
  ICompleteEpAlertToasterContentConfigObj,
  IEpAlertToasterByStatusMessageConfigObj,
  IEpAlertToasterContentConfigObj,
  IEpAlertToasterTranslations,
} from '../../interfaces';
import { EpButton } from '../EpButton';

import './EpAlertToaster.scss';

interface EpAlertToasterProps {
  customClasses?: string;
  translations: IEpAlertToasterTranslations;
}

let successDefaultTitle: string;
let warningDefaultTitle: string;
let dangerDefaultTitle: string;
let alertToasterContainerBaseId = 'ep-alert-toaster__container';
let alertToasterPlacementDefaultOption: 'top-right';
let alertToasterCustomClasses: string;
let alertToasterButtonId: string;
let alertToasterButtonOnClickFunc: () => void;

export const showAlertToaster = (
  alertToasterInfos: IEpAlertToasterInfos,
): void => {
  const { event, ...alertToasterContentConfigObj } = alertToasterInfos;

  if (event) {
    event.preventDefault();
  }

  const alertToasterSuffixId =
    document.querySelectorAll('.ep-alert-toaster').length;
  const alertToasterPlacement =
    alertToasterContentConfigObj.alertToasterPlacement ||
    alertToasterPlacementDefaultOption;
  const alertToasterContainerId = `${alertToasterContainerBaseId}--${alertToasterPlacement}`;
  const treatedAlertToasterAutoClose =
    alertToasterContentConfigObj.alertToasterAutoClose === undefined
      ? true
      : alertToasterContentConfigObj.alertToasterAutoClose;

  createAlertToasterContainerElement(alertToasterContainerId);
  cleanAlertToasterContainerElement(alertToasterContainerId);

  const completeAlertToasterContentConfigObj: ICompleteEpAlertToasterContentConfigObj =
  {
    ...alertToasterContentConfigObj,
    alertToasterAutoClose: treatedAlertToasterAutoClose,
    alertToasterDisplayDuration:
      alertToasterContentConfigObj.alertToasterDisplayDuration || 5000,
    alertToasterShouldShowSuccess:
      alertToasterContentConfigObj.alertToasterShouldShowSuccess || true,
    alertToasterSuffixId,
  };

  const alertToasterContainerElement = document.querySelector(
    `#${alertToasterContainerId}`,
  );
  const alertToasterElement = transformStringIntoHTML(
    getAlertToasterStructure(completeAlertToasterContentConfigObj),
  );

  if (!alertToasterElement) return;

  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { Toast } = require('bootstrap');
  const alertToasterElementInstance = new Toast(alertToasterElement as Element);
  alertToasterContainerElement?.prepend(alertToasterElement);

  alertToasterElementInstance.show();

  const alertToasterButtonElement: HTMLButtonElement = document.querySelector(
    `#${alertToasterButtonId}`,
  ) as HTMLButtonElement;

  if (!alertToasterButtonElement) return;

  alertToasterButtonElement.onclick = () => alertToasterButtonOnClickFunc();
  alertToasterElementInstance.show();

  if (treatedAlertToasterAutoClose) {
    setTimeout(() => {
      alertToasterElementInstance.hide();
    }, completeAlertToasterContentConfigObj.alertToasterDisplayDuration);
  }
};

export const showAlertToasterByStatusMessage = (
  alertToasterByStatusMessageConfigObj: IEpAlertToasterByStatusMessageConfigObj,
) => {
  const {
    event,
    response,
    alertToasterShouldShowSuccess,
    alertToasterTitle,
    alertToasterContent,
  } = alertToasterByStatusMessageConfigObj;
  const errorCode = response.status;
  const alertToasterCustomOptionsObj: IEpAlertToasterContentConfigObj = {
    alertToasterContent: alertToasterContent || `<p>${response.message}</p>`,
    alertToasterAutoClose: true,
    alertToasterDisplayDuration: 3000,
    alertToasterPlacement: 'top-right',
  } as IEpAlertToasterContentConfigObj;

  if (
    !alertToasterShouldShowSuccess &&
    (errorCode === 200 || errorCode === 204)
  )
    return null;

  switch (errorCode) {
    case 200:
      return showAlertToaster({
        event,
        ...alertToasterCustomOptionsObj,
        alertToasterStatus: 'success',
        alertToasterTitle: alertToasterTitle || successDefaultTitle,
      });
    case 204:
      return showAlertToaster({
        event,
        ...alertToasterCustomOptionsObj,
        alertToasterStatus: 'success',
        alertToasterTitle: alertToasterTitle || successDefaultTitle,
      });
    case 422:
      return showAlertToaster({
        ...alertToasterCustomOptionsObj,
        alertToasterStatus: 'warning',
        alertToasterTitle: alertToasterTitle || warningDefaultTitle,
      });
    default:
      return showAlertToaster({
        event,
        ...alertToasterCustomOptionsObj,
        alertToasterStatus: 'danger',
        alertToasterTitle: alertToasterTitle || dangerDefaultTitle,
      });
  }
};

const getAlertToasterStructure = (
  alertToasterProps: ICompleteEpAlertToasterContentConfigObj,
): string => {
  alertToasterButtonId = `ep-alert-toaster__footer-button--${alertToasterProps.alertToasterSuffixId}`;

  alertToasterButtonOnClickFunc = alertToasterProps.buttonOnClickFunc;

  return renderToStaticMarkup(
    <div
      className={`toast ep-alert-toaster ep-alert-toaster--${alertToasterProps.alertToasterStatus} ${alertToasterCustomClasses}`}
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      data-bs-autohide={`${alertToasterProps.alertToasterAutoClose}`}
      {...(alertToasterProps.alertToasterAutoClose && {
        'data-bs-delay': alertToasterProps.alertToasterDisplayDuration,
      })}
    >
      <div className="ep-alert-toaster__header">
        <h3 className="ep-alert-toaster__header-title">
          {alertToasterProps.alertToasterTitle}
        </h3>

        <EpButton
          buttonId={`ep-alert-toaster__header-button--close-${alertToasterProps.alertToasterSuffixId}`}
          customClasses="ep-alert-toaster__header-button"
          buttonType="link-secondary"
          buttonSize="sm"
          buttonDataBsDismiss="toast"
          buttonAriaLabel={`Botão fechar do alert-toaster ${alertToasterProps.alertToasterSuffixId}`}
          buttonLeftIconName="SolidTimes"
          buttonOnClickFunc={() => console.log()}
        />
      </div>

      {alertToasterProps.alertToasterContent && (
        <div
          className="ep-alert-toaster__content"
          dangerouslySetInnerHTML={{
            __html: alertToasterProps.alertToasterContent,
          }}
        ></div>
      )}

      {alertToasterProps.buttonText && (
        <div className="ep-alert-toaster__footer">
          <EpButton
            buttonId={alertToasterButtonId}
            customClasses="ep-alert-toaster__footer-button"
            buttonType="link-primary"
            buttonSize="md"
            buttonText={`${alertToasterProps.buttonText || ''}`}
            buttonLeftIconName={`${alertToasterProps.buttonLeftIconName || ''}`}
            buttonRightIconName={`${alertToasterProps.buttonRightIconName || ''
              }`}
            buttonOnClickFunc={alertToasterProps.buttonOnClickFunc}
            buttonAriaLabel={`CTA do alert-toaster ${alertToasterProps.alertToasterSuffixId}`}
          />
        </div>
      )}

      {alertToasterProps.alertToasterAutoClose && (
        <div className="ep-alert-toaster__timeout-bar-wrapper">
          <div
            id={`ep-alert-toaster__timeout-bar--${alertToasterProps.alertToasterSuffixId}`}
            className="ep-alert-toaster__timeout-bar"
            style={{
              animationDuration: `${alertToasterProps.alertToasterDisplayDuration}ms`,
            }}
          ></div>
        </div>
      )}
    </div>,
  );
};

const createAlertToasterContainerElement = (
  alertToasterContainerId: string,
): void => {
  if (document.querySelector(`#${alertToasterContainerId}`)) return;

  const alertToasterContainerElement = transformStringIntoHTML(
    `<div id=${alertToasterContainerId} class="toast-container ${alertToasterContainerBaseId}"></div>'}`,
  );
  document.body.append(alertToasterContainerElement as unknown as Node);
};

const cleanAlertToasterContainerElement = (
  alertToasterContainerId: string,
): void => {
  const alertToastersHiddenToRemove = document
    .getElementById(alertToasterContainerId)
    ?.querySelectorAll('.ep-alert-toaster.hide');
  alertToastersHiddenToRemove?.forEach((alertToaster) => alertToaster.remove());
};

export const EpAlertToaster: React.FC<EpAlertToasterProps> = ({
  customClasses = '',
  translations = {
    successDefaultTitle: '', // 'Success!'
    warningDefaultTitle: '', // 'Warning!'
    dangerDefaultTitle: '', // 'Error!'
  },
}) => {
  alertToasterCustomClasses = customClasses;
  successDefaultTitle = translations.successDefaultTitle;
  warningDefaultTitle = translations.warningDefaultTitle;
  dangerDefaultTitle = translations.dangerDefaultTitle;
  alertToasterContainerBaseId = 'ep-alert-toaster__container';
  alertToasterPlacementDefaultOption = 'top-right';

  return <></>;
};
